import React, { useState, useEffect } from 'react'
import { useApi } from '../../contexts/ApiContext'
import { useUserId } from '../../hooks/useUserId'
import { supabase } from '../../lib/supabase'
import toast from 'react-hot-toast'
import { Activity, RefreshCw, Search, Filter, ChevronDown, ChevronUp, Code, Clock, CheckCircle, XCircle, Trash2, Database } from 'lucide-react'
import { apiLogger } from '../../utils/apiLogger'

const ApiLogs = () => {
  const { userId } = useUserId()
  const [logs, setLogs] = useState([])
  const [dbLogs, setDbLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // 'all', 'success', 'error'
  const [typeFilter, setTypeFilter] = useState('all') // Default to all types
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedLog, setExpandedLog] = useState(null)
  const [showSource, setShowSource] = useState('all') // Default to all logs (session + database)

  // Load logs on mount and listen for updates
  useEffect(() => {
    loadAllLogs()

    // Listen for new log entries - only for this component instance
    const handleLogUpdate = (event) => {
      // Only update if the event is meant for this component
      if (event.detail && event.detail.componentId === 'apiLogs') {
        setLogs(apiLogger.getLogs())
      }
    }

    const handleLogsCleared = (event) => {
      // Only clear if the event is meant for this component
      if (event.detail && event.detail.componentId === 'apiLogs') {
        setLogs([])
      }
    }

    window.addEventListener('apiLogUpdated', handleLogUpdate)
    window.addEventListener('apiLogsCleared', handleLogsCleared)

    return () => {
      window.removeEventListener('apiLogUpdated', handleLogUpdate)
      window.removeEventListener('apiLogsCleared', handleLogsCleared)
    }
  }, [userId])
  
  // Load logs from both session storage and database
  const loadAllLogs = async () => {
    setLoading(true)
    
    // Load session storage logs
    const storedLogs = apiLogger.getLogs()
    setLogs(storedLogs)
    
    // Load DevRev logs from Supabase database
    if (userId) {
      try {
        const { data, error } = await supabase
          .from('api_logs')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(100)
        
        if (error) {
          console.error('Error loading logs from database:', error)
        } else {
          // Transform database logs to match our format
          const transformedDbLogs = (data || []).map(log => ({
            id: `db-${log.id}`,
            timestamp: log.created_at,
            method: log.method,
            endpoint: log.endpoint,
            requestHeaders: log.request_headers,
            requestBody: log.request_body,
            status: log.response_status,
            responseHeaders: log.response_headers,
            responseBody: log.response_body,
            duration: log.response_time_ms,
            type: log.api_type,
            dbId: log.id // Store original DB ID for deletion
          }))
          setDbLogs(transformedDbLogs)
        }
      } catch (e) {
        console.error('Error fetching logs:', e)
      }
    }
    
    setLoading(false)
  }

  const loadApiLogs = () => {
    loadAllLogs()
  }
  
  const clearLogs = async () => {
    if (confirm('Are you sure you want to delete all API logs? This will clear both session and database logs.')) {
      // Clear session logs
      apiLogger.clearLogs()

      // Dispatch event with componentId for specific handling
      const clearEvent = new CustomEvent('apiLogsCleared', {
        detail: { componentId: 'apiLogs' }
      })
      window.dispatchEvent(clearEvent)

      setLogs([])

      // Clear database logs
      if (userId) {
        try {
          const { error } = await supabase
            .from('api_logs')
            .delete()
            .eq('user_id', userId)

          if (error) {
            console.error('Error deleting database logs:', error)
            toast.error('Session logs cleared, but failed to delete database logs: ' + error.message)
          } else {
            setDbLogs([])
            toast.success('All logs cleared successfully')
            console.log('✅ All logs cleared from session and database')
          }
        } catch (e) {
          console.error('Error:', e)
          toast.error('Session logs cleared, but error deleting database logs: ' + e.message)
        }
      }
    }
  }
  
  const clearDbLogs = async () => {
    if (confirm('Are you sure you want to delete all DevRev logs from the database? This cannot be undone.')) {
      try {
        const { error } = await supabase
          .from('api_logs')
          .delete()
          .eq('user_id', userId)
        
        if (error) {
          toast.error('Error deleting logs: ' + error.message)
        } else {
          setDbLogs([])
          toast.success('All database logs deleted successfully')
        }
      } catch (e) {
        toast.error('Error: ' + e.message)
      }
    }
  }
  
  const deleteLog = async (log) => {
    // Check if it's a database log or session log
    if (log.dbId) {
      // Delete from database
      try {
        const { error } = await supabase
          .from('api_logs')
          .delete()
          .eq('id', log.dbId)
        
        if (error) {
          toast.error('Error deleting log: ' + error.message)
        } else {
          setDbLogs(dbLogs.filter(l => l.dbId !== log.dbId))
          toast.success('Log deleted successfully')
        }
      } catch (e) {
        toast.error('Error: ' + e.message)
      }
    } else {
      // Delete from session storage
      const updatedLogs = logs.filter(l => l.id !== log.id)
      setLogs(updatedLogs)
      sessionStorage.setItem('apiLogs', JSON.stringify(updatedLogs))
    }
  }

  // Combine logs from session and database
  const allLogs = [
    ...logs.map(log => ({ ...log, source: 'session' })),
    ...dbLogs.map(log => ({ ...log, source: 'database' }))
  ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
  
  const filteredLogs = allLogs.filter(log => {
    // Filter by source
    if (showSource === 'session' && log.source !== 'session') return false
    if (showSource === 'database' && log.source !== 'database') return false
    
    // Filter by status
    if (filter === 'success' && (log.status < 200 || log.status >= 300)) return false
    if (filter === 'error' && log.status >= 200 && log.status < 300) return false
    
    // Filter by type
    if (typeFilter !== 'all' && log.type !== typeFilter) return false
    
    // Filter by search term
    if (searchTerm && !log.endpoint.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false
    }
    
    return true
  })

  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return 'text-green-600 bg-green-100'
    if (status >= 400) return 'text-red-600 bg-red-100'
    return 'text-yellow-600 bg-yellow-100'
  }

  const getTypeColor = (type) => {
    if (type === 'DevRev') return 'text-purple-600 bg-purple-100'
    if (type === 'Supabase') return 'text-blue-600 bg-blue-100'
    return 'text-gray-600 bg-gray-100'
  }

  const toggleLogDetails = (logId) => {
    setExpandedLog(expandedLog === logId ? null : logId)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Activity className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading API logs...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">API Call Logs</h1>
          <p className="text-xs sm:text-sm text-gray-600">Track and monitor API calls for debugging</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <button
            onClick={clearLogs}
            className="flex items-center gap-1 sm:gap-2 bg-red-600 hover:bg-red-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-colors text-xs sm:text-sm whitespace-nowrap"
            title="Clear temporary session logs"
          >
            <XCircle className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Clear Session Logs</span>
            <span className="sm:hidden">Clear</span>
          </button>
          <button
            onClick={loadApiLogs}
            className="flex items-center gap-1 sm:gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-colors text-xs sm:text-sm whitespace-nowrap"
          >
            <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="success">Success (2xx)</option>
                <option value="error">Errors (4xx, 5xx)</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Activity className="h-4 w-4 text-gray-500" />
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="DevRev">DevRev (PLuG)</option>
                <option value="Supabase">Supabase</option>
                <option value="API">Other APIs</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Database className="h-4 w-4 text-gray-500" />
              <select
                value={showSource}
                onChange={(e) => setShowSource(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Sources</option>
                <option value="database">Database Only</option>
                <option value="session">Session Only</option>
              </select>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 flex-1 md:max-w-md">
            <Search className="h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search endpoints..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Total Calls</p>
          <p className="text-2xl font-bold text-gray-900">{allLogs.length}</p>
          <p className="text-xs text-gray-500 mt-1">Session: {logs.length} | DB: {dbLogs.length}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Successful</p>
          <p className="text-2xl font-bold text-green-600">
            {allLogs.filter(l => l.status >= 200 && l.status < 300).length}
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Errors</p>
          <p className="text-2xl font-bold text-red-600">
            {allLogs.filter(l => l.status >= 400).length}
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">DevRev Calls</p>
          <p className="text-2xl font-bold text-purple-600">
            {allLogs.filter(l => l.type === 'DevRev').length}
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Avg Response Time</p>
          <p className="text-2xl font-bold text-blue-600">
            {allLogs.length > 0 
              ? Math.round(allLogs.reduce((sum, l) => sum + (l.duration || 0), 0) / allLogs.length) 
              : 0}ms
          </p>
        </div>
      </div>

      {/* Logs Display - Cards for Mobile, Table for Desktop */}
      <div className="space-y-4">
        {/* Mobile View - Cards */}
        <div className="lg:hidden space-y-4">
          {filteredLogs.length > 0 ? (
            filteredLogs.map((log) => (
              <div key={log.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(log.type)}`}>
                        {log.type}
                      </span>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(log.status)}`}>
                        {log.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mb-1">
                      {new Date(log.timestamp).toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-900 break-all mb-2">
                      {log.endpoint.replace(/^https?:\/\//, '')}
                    </p>
                    <p className="text-xs text-gray-600">
                      Duration: <span className="font-semibold">{log.duration}ms</span>
                    </p>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                      className="p-2 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                      title={expandedLog === log.id ? "Hide Details" : "Show Details"}
                    >
                      {expandedLog === log.id ? (
                        <ChevronUp className="h-5 w-5 text-blue-600" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-blue-600" />
                      )}
                    </button>
                    <button
                      onClick={() => deleteLog(log)}
                      className="p-2 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                      title={log.dbId ? "Delete log from database" : "Delete session log"}
                    >
                      <Trash2 className="h-5 w-5 text-red-600" />
                    </button>
                  </div>
                </div>
                
                {/* Expanded Details */}
                {expandedLog === log.id && (
                  <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                    {/* Full URL */}
                    <div>
                      <h4 className="text-xs font-semibold text-gray-700 mb-1">Full URL</h4>
                      <div className="bg-gray-50 border border-gray-200 rounded p-2">
                        <code className="text-xs text-gray-800 break-all">{log.endpoint}</code>
                      </div>
                    </div>
                    
                    {/* Request Body */}
                    {log.requestBody && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-700 mb-1">Request Body</h4>
                        <div className="bg-gray-50 border border-gray-200 rounded p-2 max-h-40 overflow-auto">
                          <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all">
                            {typeof log.requestBody === 'string' 
                              ? log.requestBody 
                              : JSON.stringify(log.requestBody, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                    
                    {/* Response Body */}
                    {log.responseBody && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-700 mb-1">Response Body</h4>
                        <div className="bg-gray-50 border border-gray-200 rounded p-2 max-h-60 overflow-auto">
                          <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all">
                            {typeof log.responseBody === 'string' 
                              ? log.responseBody 
                              : JSON.stringify(log.responseBody, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
              <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No API logs found</p>
            </div>
          )}
        </div>
        
        {/* Desktop View - Table */}
        <div className="hidden lg:block bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Endpoint
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLogs.length > 0 ? (
                filteredLogs.map((log) => (
                  <React.Fragment key={log.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(log.type)}`}>
                          {log.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs sm:max-w-md break-all">
                        {log.endpoint.replace(/^https?:\/\//, '')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(log.status)}`}>
                          {log.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.duration}ms
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => toggleLogDetails(log.id)}
                          className="text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                        >
                          {expandedLog === log.id ? (
                            <>
                              <ChevronUp className="h-4 w-4" />
                              <span>Hide</span>
                            </>
                          ) : (
                            <>
                              <ChevronDown className="h-4 w-4" />
                              <span>View</span>
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center space-x-2">
                          {log.source === 'session' ? (
                            <button
                              onClick={() => deleteLog(log)}
                              className="text-red-600 hover:text-red-800 flex items-center space-x-1"
                              title="Delete from session (temporary log)"
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="text-xs">Delete</span>
                            </button>
                          ) : (
                            <div className="flex items-center space-x-1 text-purple-600" title="Stored in database (permanent)">
                              <Database className="h-4 w-4" />
                              <span className="text-xs">Permanent</span>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedLog === log.id && (
                      <tr>
                        <td colSpan="7" className="px-6 py-4 bg-gray-50">
                          <div className="space-y-4">
                            {/* Full URL */}
                            <div>
                              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
                                <Code className="h-4 w-4" />
                                <span>Full URL</span>
                              </h4>
                              <div className="bg-white border border-gray-200 rounded p-3">
                                <code className="text-xs text-gray-800 break-all">{log.endpoint}</code>
                              </div>
                            </div>
                            
                            {/* Request Details */}
                            <div>
                              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
                                <Clock className="h-4 w-4" />
                                <span>Request Details</span>
                              </h4>
                              <div className="bg-white border border-gray-200 rounded p-3 space-y-2">
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div>
                                    <span className="font-semibold">Method:</span> {log.method}
                                  </div>
                                  <div>
                                    <span className="font-semibold">Duration:</span> {log.duration}ms
                                  </div>
                                  <div>
                                    <span className="font-semibold">Status:</span> {log.status}
                                  </div>
                                  <div>
                                    <span className="font-semibold">Type:</span> {log.type}
                                  </div>
                                </div>
                                
                                {/* Request Headers */}
                                {log.requestHeaders && Object.keys(log.requestHeaders).length > 0 && (
                                  <div className="mt-3">
                                    <h5 className="text-xs font-semibold text-gray-600 mb-1">Request Headers:</h5>
                                    <div className="bg-gray-50 border border-gray-200 rounded p-2 max-h-40 overflow-auto">
                                      <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all overflow-wrap-anywhere">
                                        {JSON.stringify(log.requestHeaders, null, 2)}
                                      </pre>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Request Body */}
                                {log.requestBody && (
                                  <div className="mt-3">
                                    <h5 className="text-xs font-semibold text-gray-600 mb-1">Request Body:</h5>
                                    <div className="bg-gray-50 border border-gray-200 rounded p-2 max-h-60 overflow-auto">
                                      <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all overflow-wrap-anywhere">
                                        {typeof log.requestBody === 'string' 
                                          ? log.requestBody 
                                          : JSON.stringify(log.requestBody, null, 2)}
                                      </pre>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Response Details */}
                            <div>
                              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
                                {log.status >= 200 && log.status < 300 ? (
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                ) : (
                                  <XCircle className="h-4 w-4 text-red-600" />
                                )}
                                <span>Response Details</span>
                              </h4>
                              <div className="bg-white border border-gray-200 rounded p-3 space-y-2">
                                {/* Response Headers */}
                                {log.responseHeaders && Object.keys(log.responseHeaders).length > 0 && (
                                  <div>
                                    <h5 className="text-xs font-semibold text-gray-600 mb-1">Response Headers:</h5>
                                    <div className="bg-gray-50 border border-gray-200 rounded p-2 max-h-40 overflow-auto">
                                      <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all overflow-wrap-anywhere">
                                        {JSON.stringify(log.responseHeaders, null, 2)}
                                      </pre>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Response Body */}
                                {log.responseBody && (
                                  <div className="mt-3">
                                    <h5 className="text-xs font-semibold text-gray-600 mb-1">Response Body:</h5>
                                    <div className="bg-gray-50 border border-gray-200 rounded p-2 max-h-96 overflow-auto">
                                      <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all overflow-wrap-anywhere">
                                        {typeof log.responseBody === 'string' 
                                          ? log.responseBody 
                                          : JSON.stringify(log.responseBody, null, 2)}
                                      </pre>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No API logs found</p>
                    <p className="text-sm text-gray-400 mt-2">
                      {searchTerm ? 'Try adjusting your search terms' : 'API calls will appear here as you use the application'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">About API Logs</h3>
        <p className="text-sm text-blue-800">
          This page shows API calls made from your browser to the backend services including Supabase and DevRev. 
          The logs are captured from the browser's Performance API and show resource timing information.
        </p>
      </div>
    </div>
  )
}

export default ApiLogs

